import { useEffect, useMemo, useState } from "react";
import { Maximize, Minimize } from "lucide-react";

export const FALDA_COLORS = ["#FDD003", "#22D3EE", "#C084FC", "#4ADE80", "#FB923C", "#F472B6", "#A3E635", "#2DD4BF"];
const ANNOT = {
  cabina: { color: "#F97316", label: "Cabina elettrica" },
  cavidotto: { color: "#38BDF8", label: "Cavidotti" },
  locale_tecnico: { color: "#A855F7", label: "Locale tecnico / inverter" },
  parcheggio: { color: "#22C55E", label: "Area parcheggio mezzi di sollevamento" },
};

export const roofBgUrl = (token) => `${process.env.REACT_APP_BACKEND_URL}/api/public/roof-bg/${token}`;
export const hasPdfBg = (bg) => !!(bg?.png_token && bg?.mpp && bg?.imgW && bg?.imgH);
export const hasDrawing = (roof) => hasPdfBg(roof?.pdf_bg) || !!roof?.draw_mode;

// Rettangolo (in px dell'SVG editor) della tavola PDF, dati toM (lat/lng→metri) e px (metri→px) dell'editor.
export function pdfBgImage(roof, toM, px) {
  const bg = roof?.pdf_bg; const o = bg?.origin || roof?.center;
  if (!hasPdfBg(bg) || !o) return null;
  const om = toM(o), p0 = px(om), p1 = px({ x: om.x + bg.imgW * bg.mpp, y: om.y - bg.imgH * bg.mpp });
  return { href: roofBgUrl(bg.png_token), x: p0.x, y: p0.y, width: p1.x - p0.x, height: p1.y - p0.y };
}

export function EditorGridBg({ geo }) {
  const o = geo.px({ x: 0, y: 0 }), u = geo.px({ x: 1, y: 0 });
  const ppm = Math.abs(u.x - o.x);
  const id = `eg${Math.round(ppm * 100)}`;
  return (
    <g data-testid="editor-grid-bg">
      <defs><pattern id={id} x={o.x} y={o.y} width={ppm * 5} height={ppm * 5} patternUnits="userSpaceOnUse">
        {[0, 1, 2, 3, 4].map((k) => <g key={k}><line x1={k * ppm} y1={0} x2={k * ppm} y2={ppm * 5} stroke={k ? "#E2E8F0" : "#94A3B8"} strokeWidth="1" /><line x1={0} y1={k * ppm} x2={ppm * 5} y2={k * ppm} stroke={k ? "#E2E8F0" : "#94A3B8"} strokeWidth="1" /></g>)}
      </pattern></defs>
      <rect x="0" y="0" width={geo.W} height={geo.H} fill="#fff" />
      {ppm >= 3 && <rect x="0" y="0" width={geo.W} height={geo.H} fill={`url(#${id})`} />}
    </g>
  );
}

function MeterGrid({ vb }) {
  const lines = [];
  const x0 = Math.floor(vb.x), x1 = Math.ceil(vb.x + vb.w), y0 = Math.floor(vb.y), y1 = Math.ceil(vb.y + vb.h);
  for (let x = x0; x <= x1; x++) lines.push(<line key={`x${x}`} x1={x} y1={y0} x2={x} y2={y1} stroke={x % 5 ? "#E2E8F0" : "#94A3B8"} strokeWidth={1} vectorEffect="non-scaling-stroke" />);
  for (let y = y0; y <= y1; y++) lines.push(<line key={`y${y}`} x1={x0} y1={y} x2={x1} y2={y} stroke={y % 5 ? "#E2E8F0" : "#94A3B8"} strokeWidth={1} vectorEffect="non-scaling-stroke" />);
  return <g data-testid="roof-drawing-grid"><rect x={vb.x} y={vb.y} width={vb.w} height={vb.h} fill="#fff" />{lines}</g>;
}

function sideLabels(coords, P, fs, fi) {
  const pts = (coords || []).map(P);
  return pts.map((a, i) => {
    const b = pts[(i + 1) % pts.length]; const L = Math.hypot(b.x - a.x, b.y - a.y);
    if (L < 0.3) return null;
    const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2, t = `${L.toFixed(2)} m`, w = fs * 0.62 * t.length;
    return (
      <g key={`s${fi}-${i}`} data-testid="roof-side-label">
        <rect x={mx - w / 2 - fs * 0.25} y={my - fs * 0.7} width={w + fs * 0.5} height={fs * 1.1} rx={fs * 0.15} fill="#111827" />
        <text x={mx} y={my + fs * 0.12} fontSize={fs * 0.8} fontWeight="700" fill="#fff" textAnchor="middle">{t}</text>
      </g>
    );
  });
}

const signalReady = () => { try { window.__roofMapReady = "1"; document.body?.setAttribute("data-roofmap-ready", "1"); } catch (e) { /* noop */ } };

// Layout su PDF quotato: immagine della tavola in scala (metri) + falde, pannelli e annotazioni sopra.
export default function RoofPdfView({ pdfBg, origin, falde, panelLayout, annotations, panelPhoto, pdfExport = false, compact = false }) {
  const [full, setFull] = useState(false);
  const pdf = hasPdfBg(pdfBg);
  const o = (pdf && pdfBg.origin) || origin;
  const mLat = 111320, mLng = 111320 * Math.cos(((o?.lat || 0) * Math.PI) / 180);
  const P = (c) => ({ x: (c.lng - o.lng) * mLng, y: -(c.lat - o.lat) * mLat });
  const Wm = pdf ? pdfBg.imgW * pdfBg.mpp : 0, Hm = pdf ? pdfBg.imgH * pdfBg.mpp : 0;
  const pts = (arr) => (arr || []).map((c) => { const p = P(c); return `${p.x},${p.y}`; }).join(" ");

  const vb = useMemo(() => {
    if (!o) return null;
    const all = [];
    (falde || []).forEach((f) => (f.coordinates || []).forEach((c) => all.push(P(c))));
    (annotations || []).forEach((a) => (a.coordinates || []).forEach((c) => c.lat != null && all.push(P(c))));
    if (pdf && (full || !all.length)) return { x: 0, y: 0, w: Wm, h: Hm };
    if (!all.length) return { x: -10, y: -10, w: 20, h: 20 };
    const xs = all.map((p) => p.x), ys = all.map((p) => p.y);
    let x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
    const pad = Math.max(4, 0.3 * Math.max(x1 - x0, y1 - y0));
    if (!pdf) return { x: x0 - pad, y: y0 - pad, w: x1 - x0 + 2 * pad, h: y1 - y0 + 2 * pad };
    x0 = Math.max(Math.min(0, x0), x0 - pad); y0 = Math.max(Math.min(0, y0), y0 - pad);
    x1 = Math.min(Math.max(Wm, x1), x1 + pad); y1 = Math.min(Math.max(Hm, y1), y1 + pad);
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [falde, annotations, full, Wm, Hm, pdf]);
  useEffect(() => { if (pdfExport && !pdf) setTimeout(signalReady, 300); }, [pdfExport, pdf]);

  if (!o) return null;
  const fs = Math.max(vb.w, vb.h) / 45;
  const panels = panelLayout?.enabled ? (panelLayout.panels || []) : [];
  const usePhoto = !!panelPhoto && panels.length <= 600;

  return (
    <div className="relative w-full rounded-lg border border-border mb-4 overflow-hidden bg-white" data-testid="roof-map-view" data-variant="pdf">
      <svg viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`} className={`w-full block ${pdfExport ? (compact ? "max-h-[300px]" : "max-h-[430px]") : "max-h-[70vh]"}`} preserveAspectRatio="xMidYMid meet" data-testid="roof-pdf-view">
        {pdf ? <image href={roofBgUrl(pdfBg.png_token)} x={0} y={0} width={Wm} height={Hm} preserveAspectRatio="none" onLoad={() => pdfExport && setTimeout(signalReady, 300)} onError={() => pdfExport && signalReady()} />
          : <MeterGrid vb={vb} />}
        {(falde || []).map((f, i) => {
          const col = FALDA_COLORS[i % FALDA_COLORS.length];
          return <polygon key={f.id || i} points={pts(f.coordinates)} fill={col} fillOpacity={0.18} stroke={col} strokeWidth={2.5} vectorEffect="non-scaling-stroke" />;
        })}
        {panels.map((pn, i) => {
          const c = (pn.corners || []).map(P);
          if (c.length < 3) return null;
          if (usePhoto && c.length >= 4) {
            const m = `matrix(${c[1].x - c[0].x},${c[1].y - c[0].y},${c[3].x - c[0].x},${c[3].y - c[0].y},${c[0].x},${c[0].y})`;
            return <image key={i} href={panelPhoto} x={0} y={0} width={1} height={1} preserveAspectRatio="none" transform={m} />;
          }
          return <polygon key={i} points={c.map((p) => `${p.x},${p.y}`).join(" ")} fill="#1E3A8A" fillOpacity={0.85} stroke="#0B1B4D" strokeWidth={0.6} vectorEffect="non-scaling-stroke" />;
        })}
        {(falde || []).flatMap((f, i) => (f.esclusioni || []).map((e, j) => (
          <polygon key={`e${i}-${j}`} points={pts(e.coordinates)} fill="#DC2626" fillOpacity={0.35} stroke="#DC2626" strokeWidth={2} vectorEffect="non-scaling-stroke" />
        )))}
        {(annotations || []).map((a, i) => {
          const meta = ANNOT[a.tipo]; const cs = (a.coordinates || []).filter((c) => c.lat != null);
          if (!meta || !cs.length) return null;
          const p0 = P(cs[0]);
          return (
            <g key={`a${i}`}>
              {a.tipo === "cavidotto" ? <polyline points={pts(cs)} fill="none" stroke={meta.color} strokeWidth={4} vectorEffect="non-scaling-stroke" />
                : a.tipo === "parcheggio" ? <circle cx={p0.x} cy={p0.y} r={fs * 0.5} fill={meta.color} stroke="#fff" strokeWidth={2} vectorEffect="non-scaling-stroke" />
                : <polygon points={pts(cs)} fill={meta.color} fillOpacity={0.28} stroke={meta.color} strokeWidth={2} vectorEffect="non-scaling-stroke" />}
              <text x={p0.x} y={p0.y - fs * 0.6} fontSize={fs} fontWeight="700" fill={meta.color} stroke="#fff" strokeWidth={fs * 0.15} paintOrder="stroke">{a.label || meta.label}</text>
            </g>
          );
        })}
        {!pdf && (falde || []).flatMap((f, i) => sideLabels(f.coordinates, P, fs, i))}
      </svg>
      {!pdfExport && pdf && (
        <button type="button" onClick={() => setFull((v) => !v)} className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md bg-black/70 text-white text-xs px-2 py-1 hover:bg-black/85 transition-colors" data-testid="roof-pdf-view-toggle">
          {full ? <Minimize className="h-3.5 w-3.5" /> : <Maximize className="h-3.5 w-3.5" />} {full ? "Zoom sul layout" : "Tavola intera"}
        </button>
      )}
    </div>
  );
}
